/**
 * The tile LRU (CLAUDE.md: "Tile cache: LRU, ≤ 250 MB, backed by the Cache API for revisits").
 *
 * ## What it is actually protecting
 *
 * The number that matters is not the JS heap — it is GPU memory, and nothing in the browser
 * will tell you when you are about to exhaust it. A 256×256 RGBA texture is 256 KB, a meshed
 * tile another ~100 KB of vertex buffers, and a 200 km draw distance at the Ultra tier wants
 * thousands of them. Left unbounded, a long flight ends with the tab being killed by the OS,
 * usually somewhere over an interesting bit of coastline. So every entry declares its own byte
 * cost and the cache evicts, in least-recently-used order, until it is under budget.
 *
 * ## Why entries can be pinned
 *
 * Strict LRU has a failure mode here: the tiles the camera is looking at right now are exactly
 * the ones most expensive to lose, and if the budget is tight a burst of newly loaded tiles can
 * evict the tiles they were loaded to replace — the classic thrash where every frame evicts
 * what the next frame needs. Visible tiles are therefore *pinned* each frame by the terrain
 * system and cannot be evicted; only the off-screen tail is eligible. If the visible set alone
 * exceeds the budget the cache goes over rather than blanking the world, and says so through
 * {@link TileCache.overBudget} so the quality governor has something to act on.
 *
 * `updateFixed` / `updateFrame`: neither. `touch` is called during frame-time tile selection,
 * `trim` once per frame after it. Both are allocation-free in the steady state.
 */

/** Anything the cache holds must be able to say how big it is and how to let it go. */
export interface CacheableTile {
  /** Approximate GPU + heap footprint [bytes]. */
  readonly byteSize: number;
  /** Release GPU resources. Called exactly once, on eviction. */
  dispose(): void;
}

interface Entry<T extends CacheableTile> {
  key: number;
  value: T;
  /** Monotonic counter; the smallest is the least recently used. */
  usedAt: number;
  pinned: boolean;
}

export interface TileCacheOptions {
  /** Byte ceiling. CLAUDE.md sets 250 MB at the top tiers. */
  maxBytes: number;
  /** Called for every evicted entry, after `dispose()`. Diagnostics only. */
  onEvict?: (key: number, value: CacheableTile) => void;
}

export interface TileCacheStats {
  entries: number;
  bytes: number;
  maxBytes: number;
  pinned: number;
  hits: number;
  misses: number;
  evictions: number;
}

export class TileCache<T extends CacheableTile> {
  private readonly entries = new Map<number, Entry<T>>();
  private readonly onEvict: TileCacheOptions['onEvict'];

  private maxBytesValue: number;
  private bytesValue = 0;
  private clock = 0;
  private pinnedCount = 0;
  private hitCount = 0;
  private missCount = 0;
  private evictionCount = 0;

  /** Reused so the overlay can read stats every frame without allocating. */
  private readonly stats: TileCacheStats = {
    entries: 0,
    bytes: 0,
    maxBytes: 0,
    pinned: 0,
    hits: 0,
    misses: 0,
    evictions: 0,
  };

  constructor(options: TileCacheOptions) {
    this.maxBytesValue = Math.max(1, options.maxBytes);
    this.onEvict = options.onEvict;
  }

  get size(): number {
    return this.entries.size;
  }

  get bytes(): number {
    return this.bytesValue;
  }

  get maxBytes(): number {
    return this.maxBytesValue;
  }

  /** True when the pinned working set alone will not fit. The tier is too high for this GPU. */
  get overBudget(): boolean {
    return this.bytesValue > this.maxBytesValue;
  }

  /** Re-read the budget from the quality tier. Trims immediately if it shrank. */
  setMaxBytes(maxBytes: number): void {
    this.maxBytesValue = Math.max(1, maxBytes);
    this.trim();
  }

  has(key: number): boolean {
    return this.entries.has(key);
  }

  /** Look up without affecting recency — for "is this already loaded?" checks. */
  peek(key: number): T | undefined {
    return this.entries.get(key)?.value;
  }

  /** Look up and mark as recently used. */
  get(key: number): T | undefined {
    const entry = this.entries.get(key);
    if (entry === undefined) {
      this.missCount++;
      return undefined;
    }
    entry.usedAt = ++this.clock;
    this.hitCount++;
    return entry.value;
  }

  /** Mark as recently used without retrieving. */
  touch(key: number): boolean {
    const entry = this.entries.get(key);
    if (entry === undefined) return false;
    entry.usedAt = ++this.clock;
    return true;
  }

  /**
   * Insert, replacing and disposing any previous value for the same key.
   * Does **not** trim — call {@link trim} once per frame, after the visible set is pinned.
   */
  set(key: number, value: T): void {
    const existing = this.entries.get(key);
    if (existing !== undefined) {
      this.bytesValue -= existing.value.byteSize;
      if (existing.pinned) this.pinnedCount--;
      existing.value.dispose();
      this.entries.delete(key);
    }

    this.entries.set(key, { key, value, usedAt: ++this.clock, pinned: false });
    this.bytesValue += value.byteSize;
  }

  /** Protect an entry from eviction until {@link unpinAll}. Also marks it recently used. */
  pin(key: number): boolean {
    const entry = this.entries.get(key);
    if (entry === undefined) return false;
    entry.usedAt = ++this.clock;
    if (!entry.pinned) {
      entry.pinned = true;
      this.pinnedCount++;
    }
    return true;
  }

  /** Release every pin. Called at the start of each frame's tile selection. */
  unpinAll(): void {
    if (this.pinnedCount === 0) return;
    for (const entry of this.entries.values()) entry.pinned = false;
    this.pinnedCount = 0;
  }

  /**
   * Evict least-recently-used unpinned entries until the cache is inside its budget.
   *
   * Collects the eviction candidates and sorts them rather than repeatedly scanning for the
   * minimum: when the budget shrinks — which happens the moment the quality governor steps
   * down a tier — hundreds of tiles go at once, and repeated linear scans of a few thousand
   * entries turn a tier change into a visible stall.
   *
   * @returns how many entries were evicted.
   */
  trim(): number {
    if (this.bytesValue <= this.maxBytesValue) return 0;

    const candidates: Array<Entry<T>> = [];
    for (const entry of this.entries.values()) {
      if (!entry.pinned) candidates.push(entry);
    }
    candidates.sort((a, b) => a.usedAt - b.usedAt);

    let evicted = 0;
    for (const entry of candidates) {
      if (this.bytesValue <= this.maxBytesValue) break;
      this.entries.delete(entry.key);
      this.bytesValue -= entry.value.byteSize;
      entry.value.dispose();
      this.onEvict?.(entry.key, entry.value);
      this.evictionCount++;
      evicted++;
    }
    return evicted;
  }

  /** Remove one entry, disposing it. */
  delete(key: number): boolean {
    const entry = this.entries.get(key);
    if (entry === undefined) return false;
    this.entries.delete(key);
    this.bytesValue -= entry.value.byteSize;
    if (entry.pinned) this.pinnedCount--;
    entry.value.dispose();
    return true;
  }

  /** Drop everything, disposing every entry. */
  clear(): void {
    for (const entry of this.entries.values()) entry.value.dispose();
    this.entries.clear();
    this.bytesValue = 0;
    this.pinnedCount = 0;
  }

  /** Live counters for the F3 overlay. Reused object — read, do not retain. */
  statistics(): Readonly<TileCacheStats> {
    this.stats.entries = this.entries.size;
    this.stats.bytes = this.bytesValue;
    this.stats.maxBytes = this.maxBytesValue;
    this.stats.pinned = this.pinnedCount;
    this.stats.hits = this.hitCount;
    this.stats.misses = this.missCount;
    this.stats.evictions = this.evictionCount;
    return this.stats;
  }

  /** Iterate keys, least-recently-used first. Test and diagnostic use. */
  keysByAge(): number[] {
    return [...this.entries.values()].sort((a, b) => a.usedAt - b.usedAt).map((e) => e.key);
  }
}

/**
 * The Cache API layer, for tiles that survive a reload (CLAUDE.md: "backed by the Cache API for
 * revisits").
 *
 * This caches the *encoded* bytes — the PNGs and JPEGs as they came off the wire — not the
 * decoded textures, because that is what the browser can persist to disk and what saves the
 * round trip on the next session. Decoding and meshing still happen on every load, in the
 * worker, where they are off the frame path anyway.
 *
 * Every method degrades to a no-op rather than throwing. The Cache API is unavailable in
 * private windows on some browsers, absent in Node, and can reject mid-flight when the origin's
 * storage quota is exhausted — none of which is a reason for the terrain to stop working.
 */
export class TileByteCache {
  private readonly cacheName: string;
  private cache: Cache | null = null;
  private openAttempted = false;
  private availableValue = false;

  constructor(cacheName = 'skysim-tiles-v1') {
    this.cacheName = cacheName;
  }

  /** True once a cache has been opened successfully. */
  get available(): boolean {
    return this.availableValue;
  }

  private async open(): Promise<Cache | null> {
    if (this.openAttempted) return this.cache;
    this.openAttempted = true;
    try {
      const caches = (globalThis as { caches?: CacheStorage }).caches;
      if (caches === undefined) return null;
      this.cache = await caches.open(this.cacheName);
      this.availableValue = true;
    } catch {
      this.cache = null; // private mode, no quota, or no Cache API at all
    }
    return this.cache;
  }

  /** Cached response for a URL, or null. */
  async match(url: string): Promise<Response | null> {
    try {
      const cache = await this.open();
      return (await cache?.match(url)) ?? null;
    } catch {
      return null;
    }
  }

  /** Store a response. The caller keeps its own clone — this one is consumed. */
  async put(url: string, response: Response): Promise<void> {
    try {
      const cache = await this.open();
      await cache?.put(url, response);
    } catch {
      // Quota exceeded is the common case; the tile is already in memory and usable.
    }
  }

  /** Delete the whole cache — the "clear terrain cache" button in settings. */
  async clear(): Promise<void> {
    try {
      const caches = (globalThis as { caches?: CacheStorage }).caches;
      await caches?.delete(this.cacheName);
      this.cache = null;
      this.openAttempted = false;
      this.availableValue = false;
    } catch {
      // Nothing to do; the in-memory LRU is unaffected either way.
    }
  }
}
